/**
 * ActionPanel — context-sensitive bottom toolbar.
 *
 * v1 pattern: ALL buttons are always visible. Inactive buttons are dimmed.
 * This is cognitively better — the brain registers the dimmed icons and
 * they "light up" when context changes. No layout shift.
 *
 * Three states:
 *   1. `no-selection` — Add + Search active; Schedule + Move dimmed
 *   2. `tasks-selected` — Add + Schedule + Move + Delete + More active
 *   3. `task-open` — Move + Delete + More active
 *
 * Desktop: always visible, centered with generous gap.
 * Mobile: full-width, hidden in no-selection + during drag (animated).
 */

import { memo, useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useKeyboardStore } from "@/features/auth/stores/keyboardStore"
import { toast } from "sonner"
import Plus from "@/shared/icons/pikaicons/stroke/maths/plus-default.svg?react"
import Search from "@/shared/icons/totodoicons/stroke/search.svg?react"
import CalendarDays from "@/shared/icons/totodoicons/stroke/calendar.svg?react"
import FolderInput from "@/shared/icons/pikaicons/stroke/files-folders/folder-arrow-right.svg?react"
import Trash2 from "@/shared/icons/totodoicons/stroke/delete.svg?react"
import RotateCcw from "@/shared/icons/totodoicons/stroke/undo.svg?react"
import { cn } from "@/shared/utils/general/cn"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { useAndroidBack } from "@/shared/hooks/useAndroidBack"
import { BACK_PRIORITY } from "@/shared/utils/platform/backHandlerStack"
import { useViewContext } from "@/app/stores/viewContext"
import { useActiveTask } from "@/features/task-detail/hooks/useActiveTask"
import {
  addTask,
  bulkDelete,
  bulkRestore,
  moveTask,
  moveMultiple,
} from "@/db/operations"
import { db } from "@/db/schema"
import { getTaskDefaults } from "@/shared/utils/task/getTaskDefaults"
import { useSetting } from "@/features/settings/hooks/useSetting"
import { useLiveQueryImmediate } from "@/db/hooks"
import { ContainerPicker } from "@/features/task-detail/components/ContainerPicker"
import { TagPicker } from "@/features/tags/TagPicker"
import { IconPicker } from "@/features/icon-picker/IconPicker"
import { GlobalDatePicker } from "@/features/schedule/GlobalDatePicker"
import { GlobalStatusPicker } from "@/shared/components/GlobalStatusPicker"
import { ProjectStatusDialogHost } from "@/features/project-header/ProjectStatusDialogHost"
import { ActionPanelButton } from "./ActionPanelButton"
import { MoreMenu } from "./MoreMenu"
import { useActionPanelState } from "./useActionPanelState"
import { ACTION_PANEL_HEIGHT } from "./constants"

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ActionPanel = memo(function ActionPanel() {
  const { t } = useTranslation()
  const { isDesktop, isMobile } = useBreakpoint()
  const state = useActionPanelState()
  const {
    context,
    selectedTaskIds,
    openTaskId,
    viewId,
    containerId,
    visible,
    isTrash,
    isLogbook,
    allowCreate,
  } = state
  const { value: eveningHour } = useSetting<number>("#evening-time", 18)
  const { closeTask } = useActiveTask()
  const keyboardOpen = useKeyboardStore((s) => s.keyboard?.isOpen ?? false)

  // Android back button → close task detail
  const handleBackCloseTask = useCallback(() => {
    closeTask()
    return true
  }, [closeTask])
  useAndroidBack(BACK_PRIORITY.TASK_DETAIL, openTaskId != null, handleBackCloseTask, "task-detail")

  // Move picker state — driven by viewContext so keyboard shortcut can open it too
  const movePicker = useViewContext((s) => s.movePicker)
  const showMovePicker = movePicker !== null

  // Tag picker state — driven by viewContext so keyboard shortcut can open it
  const tagPicker = useViewContext((s) => s.tagPicker)

  // Icon picker state
  const iconPicker = useViewContext((s) => s.iconPicker)

  // Resolve the open task's current container for the ContainerPicker "current" badge
  const [currentContainerId] = useLiveQueryImmediate(
    async () => {
      if (!openTaskId) return null
      const task = await db.totodo.get(openTaskId)
      if (!task) return null
      const segments = task.parentPath.split("/")
      return segments.length >= 2 ? (segments[1] ?? null) : null
    },
    [openTaskId],
    null,
  )

  // -----------------------------------------------------------------------
  // Derived: is there an active selection or open task?
  // -----------------------------------------------------------------------
  const hasContext = context !== "no-selection"
  const hasScheduleTarget = selectedTaskIds.length > 0 || openTaskId != null
  const hasMoveTarget = hasScheduleTarget
  const hasDeleteTarget = hasScheduleTarget

  // Mobile: hide panel in no-selection or during drag (matches v1 shouldHide)
  const isDragging = useViewContext((s) => s.isDragging)
  const shouldHide = (isMobile && (context === "no-selection" || isDragging)) || !visible

  // Expose panel height as CSS variable so the Sonner toaster stays above it
  useEffect(() => {
    const offset = shouldHide ? "0px" : `${ACTION_PANEL_HEIGHT + 1}px`
    document.documentElement.style.setProperty("--action-panel-offset", offset)
    return () => {
      document.documentElement.style.removeProperty("--action-panel-offset")
    }
  }, [shouldHide])

  // -----------------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------------

  const handleAddTask = useCallback(async () => {
    // On routines view, dispatch event instead of creating a task
    if (viewId === "routines") {
      window.dispatchEvent(new CustomEvent("create-routine"))
      return
    }
    const defaults = getTaskDefaults(
      viewId,
      containerId ?? undefined,
      eveningHour,
    )
    await addTask({
      title: "",
      parentPath: defaults.parentPath,
      insertPosition: "top",
      startAt: defaults.schedule?.startAt,
      startAtType: defaults.schedule?.type,
      startAtSubType: defaults.schedule?.subType,
    })
  }, [viewId, containerId, eveningHour])

  const handleSearch = useCallback(() => {
    useViewContext.getState().setSearchOpen(true)
  }, [])

  const handleSchedule = useCallback(() => {
    if (selectedTaskIds.length > 0) {
      useViewContext.getState().openSchedulePicker(selectedTaskIds)
    } else if (openTaskId) {
      useViewContext.getState().openSchedulePicker([openTaskId])
    }
  }, [selectedTaskIds, openTaskId])

  const handleMove = useCallback(() => {
    const taskIds =
      selectedTaskIds.length > 0
        ? selectedTaskIds
        : openTaskId
          ? [openTaskId]
          : []
    if (taskIds.length === 0) return
    const vc = useViewContext.getState()
    if (vc.movePicker) {
      vc.closePickers()
    } else {
      vc.openMovePicker(taskIds)
    }
  }, [selectedTaskIds, openTaskId])

  const handleMoveSelect = useCallback(
    async (targetContainerId: string, targetTitle: string) => {
      const newParentPath = `root/${targetContainerId}`
      const taskIds =
        selectedTaskIds.length > 0
          ? selectedTaskIds
          : openTaskId
            ? [openTaskId]
            : []
      if (taskIds.length === 0) return

      const result =
        taskIds.length === 1
          ? await moveTask(taskIds[0]!, newParentPath)
          : await moveMultiple(taskIds, newParentPath)

      // A refused move (the target is gone, or may not hold it) moved nothing: say why, keep the
      // selection and the open task as they are, and do not announce a move that did not happen.
      if (!result.ok) {
        useViewContext.getState().closePickers()
        toast.error(result.error)
        return
      }

      // Close task detail when moving the open task (it leaves the current list)
      if (context === "task-open") {
        closeTask()
      }
      useViewContext.getState().clearSelection()
      useViewContext.getState().closePickers()
      toast(t("Moved to {{container}}", { container: targetTitle }))
    },
    [selectedTaskIds, openTaskId, context, closeTask, t],
  )

  const handleDelete = useCallback(async () => {
    const taskIds =
      selectedTaskIds.length > 0
        ? selectedTaskIds
        : openTaskId
          ? [openTaskId]
          : []
    if (taskIds.length === 0) return

    if (isTrash) {
      const result = await bulkRestore(taskIds)
      if (result.ok) {
        toast(
          taskIds.length === 1
            ? t("Task restored")
            : t("{{count}} tasks restored", { count: taskIds.length }),
        )
      }
    } else {
      const result = await bulkDelete(taskIds)
      if (result.ok) {
        toast(
          taskIds.length === 1
            ? t("Moved to trash")
            : t("{{count}} tasks moved to trash", { count: taskIds.length }),
        )
      }
    }

    // v1 pattern: close detail panel when operating on the open task
    if (context === "task-open") {
      closeTask()
    }
    useViewContext.getState().clearSelection()
  }, [selectedTaskIds, openTaskId, isTrash, context, closeTask, t])

  // -----------------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------------

  const moreMenuTaskIds =
    selectedTaskIds.length > 0
      ? selectedTaskIds
      : openTaskId
        ? [openTaskId]
        : []

  // -----------------------------------------------------------------------
  // Stable button set — always rendered, disabled when inactive (v1 pattern)
  // Left group:  [Add] [Schedule] [Move]
  // Right group: [Search] or [Delete] [More]
  // -----------------------------------------------------------------------

  // Add: visible in no-selection + tasks-selected; hidden in task-open and trash
  const showAdd = context !== "task-open" && allowCreate && !isTrash
  const addDisabled = isLogbook || isTrash

  // Schedule: visible in no-selection (dimmed) + tasks-selected (active)
  // Hidden in task-open (v1 pattern) and trash
  const showSchedule = context !== "task-open" && !isTrash
  const scheduleDisabled = !hasScheduleTarget || isLogbook

  // Move: visible in all states except trash (v1 hides in trash)
  const showMove = !isTrash
  const moveDisabled = !hasMoveTarget || isLogbook

  // Search: visible only in no-selection
  const showSearch = context === "no-selection"

  // Delete: visible when there's a context (tasks-selected or task-open)
  const showDelete = hasContext
  const deleteDisabled = !hasDeleteTarget

  // More: visible when there's a context, hidden in trash (v1 pattern)
  const showMore = hasContext && !isTrash

  return (
    <div
      data-action-panel
      className={cn(
        "shrink-0 border-t border-border/30 bg-background/95 backdrop-blur-sm",
        "flex items-center transition-[height,opacity] duration-200 ease-out",
        // Only clip overflow during hide animation; visible when shown so popovers can extend above
        shouldHide ? "overflow-hidden" : "overflow-visible",
        // Desktop: centered with generous gap
        isDesktop && "justify-center gap-[120px]",
        // Mobile: spread evenly
        !isDesktop && "justify-between px-2",
      )}
      style={{
        // Animated height for show/hide (Fix D)
        height: shouldHide ? 0 : ACTION_PANEL_HEIGHT,
        opacity: shouldHide ? 0 : 1,
        // Mobile native: add safe area padding when visible AND keyboard closed.
        // When keyboard is up the WebView shrinks (KeyboardResize.Native) so
        // the home indicator area is behind the keyboard — no safe area needed.
        ...(!isDesktop &&
          !shouldHide &&
          !keyboardOpen && {
            paddingBottom: "var(--safe-area-bottom, 0px)",
            height: `calc(${ACTION_PANEL_HEIGHT}px + var(--safe-area-bottom, 0px))`,
          }),
        // Remove border when hidden
        borderTopWidth: shouldHide ? 0 : undefined,
      }}
      role="toolbar"
      aria-label={t("Actions")}
    >
      {/* --- Left group: Add + Schedule + Move --- */}
      {showAdd && (
        <ActionPanelButton
          icon={<Plus />}
          label={viewId === "routines" ? t("New Routine") : t("Add Task")}
          onClick={handleAddTask}
          disabled={addDisabled}
          shortcut="N"
        />
      )}
      {showSchedule && (
        <ActionPanelButton
          icon={<CalendarDays />}
          label={t("Schedule")}
          onClick={handleSchedule}
          disabled={scheduleDisabled}
        />
      )}
      {showMove && (
        <ActionPanelButton
          icon={<FolderInput />}
          label={t("Move")}
          onClick={handleMove}
          disabled={moveDisabled}
        />
      )}

      {/* Move dialog — rendered via portal, position-independent */}
      <ContainerPicker
        open={showMovePicker}
        onOpenChange={(open) => {
          if (!open) useViewContext.getState().closePickers()
        }}
        onSelect={handleMoveSelect}
        currentContainerId={currentContainerId ?? undefined}
        showVirtualContainers
      />

      {/* Tag picker dialog — opened via Cmd+Shift+T or programmatically */}
      <TagPicker
        taskIds={tagPicker?.taskIds ?? []}
        open={tagPicker !== null}
        onOpenChange={(open) => {
          if (!open) useViewContext.getState().closePickers()
        }}
      />

      {/* Icon picker dialog — opened via sidebar context menu */}
      <IconPicker
        containerId={iconPicker?.containerId ?? null}
        open={iconPicker !== null}
        onOpenChange={(open) => {
          if (!open) useViewContext.getState().closePickers()
        }}
      />

      {/* Schedule/Deadline picker — opened via When/Deadline in context menus */}
      <GlobalDatePicker />

      {/* Status picker dialog — opened via Cmd+Y */}
      <GlobalStatusPicker />

      {/* Complete/cancel project question: asked from the header, sidebar, task menus, checkboxes */}
      <ProjectStatusDialogHost />

      {/* --- Right group: Search + Delete + More --- */}
      {showSearch && (
        <ActionPanelButton
          icon={<Search />}
          label={t("Search")}
          onClick={handleSearch}
          shortcut="/"
        />
      )}
      {showDelete && (
        <ActionPanelButton
          icon={isTrash ? <RotateCcw /> : <Trash2 />}
          label={isTrash ? t("Restore") : t("Delete")}
          onClick={handleDelete}
          disabled={deleteDisabled}
          destructive={!isTrash}
        />
      )}
      {showMore && <MoreMenu context={context} taskIds={moreMenuTaskIds} />}
    </div>
  )
})
